import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import "./FloatingToolbar.scss";
import { MODES } from "../NodeEditor";

import HandStop from "./assets/Hand Stop.svg";
import Select from "./assets/Select.svg";
import Undo from "./assets/Undo.svg";
import Redo from "./assets/Redo.svg";

const DEFAULT_SIDE_PADDING = 24;
const DEFAULT_BOTTOM_PADDING = 24;
const DEFAULT_TIMELINE_GAP = 28;
const DRAG_START_THRESHOLD = 4;

// A floating toolbar that starts initially in the center on the x axis and 3 quarters down on the y axis.
// The toolbar can be dragged around within the bounds of its parent container.
const FloatingToolbar = (props) => {
    const [toolbarX, setToolbarX] = useState(props.initialX ?? window.innerWidth / 2);
    const [toolbarY, setToolbarY] = useState(props.initialY ?? window.innerHeight * 0.75);
    const [hasBeenManuallyMoved, setHasBeenManuallyMoved] = useState(false);

    const switchMode = (mode) => {
        if (props.setCurrentMode) {
            props.setCurrentMode(mode);
        }
    };

    const isCombinedMode = ![
        MODES.FLOW_EDITOR,
        MODES.CANVAS_EDITOR,
        MODES.EXPANDED_FLOW,
    ].includes(props.currentMode);

    const canOpenComponentFlow = Boolean(props.canOpenComponentFlow);
    const canOpenCanvas = Boolean(props.canOpenCanvas);

    const handleComponentFlowClick = (e) => {
        e.stopPropagation();
        if (isCombinedMode || !canOpenComponentFlow || !props.onOpenComponentFlow) {
            return;
        }
        props.onOpenComponentFlow();
    };

    const isDragging = useRef(false);
    const dragStartedRef = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const pointerDownStartRef = useRef({ x: 0, y: 0 });
    const toolbarRef = useRef(null);

    const getConstrainedPosition = useCallback((desiredX, desiredY) => {
        const toolbarElement = toolbarRef.current;
        const parentElement = toolbarElement?.parentElement;

        if (!toolbarElement || !parentElement) {
            return null;
        }

        const parentRect = parentElement.getBoundingClientRect();
        const toolbarWidth = toolbarElement.offsetWidth || 0;
        const toolbarHeight = toolbarElement.offsetHeight || 0;
        const minCenterX = (toolbarWidth / 2) + DEFAULT_SIDE_PADDING;
        const maxCenterX = parentRect.width - (toolbarWidth / 2) - DEFAULT_SIDE_PADDING;
        const minCenterY = (toolbarHeight / 2) + DEFAULT_BOTTOM_PADDING;
        const maxCenterY = parentRect.height - (toolbarHeight / 2) - DEFAULT_BOTTOM_PADDING;

        return {
            x: Math.min(Math.max(desiredX, minCenterX), Math.max(minCenterX, maxCenterX)),
            y: Math.min(Math.max(desiredY, minCenterY), Math.max(minCenterY, maxCenterY)),
        };
    }, []);

    const getAutoLockedPosition = useCallback(() => {
        const toolbarElement = toolbarRef.current;
        const parentElement = toolbarElement?.parentElement;

        if (!toolbarElement || !parentElement) {
            return null;
        }

        const parentRect = parentElement.getBoundingClientRect();
        const toolbarHeight = toolbarElement.offsetHeight || 0;
        const centeredX = parentRect.width / 2;
        const centerY = props.currentMode === MODES.EXPANDED_FLOW
            ? parentRect.height - (toolbarHeight / 2) - DEFAULT_BOTTOM_PADDING
            : parentRect.height - (Number(props.timelineHeight) || 0) - (toolbarHeight / 2) - DEFAULT_TIMELINE_GAP;

        return getConstrainedPosition(centeredX, centerY);
    }, [getConstrainedPosition, props.currentMode, props.timelineHeight]);

    const syncAutoLockedPosition = useCallback(() => {
        if (hasBeenManuallyMoved) {
            return;
        }

        const nextPosition = getAutoLockedPosition();
        if (!nextPosition) {
            return;
        }

        setToolbarX(nextPosition.x);
        setToolbarY(nextPosition.y);
    }, [getAutoLockedPosition, hasBeenManuallyMoved]);

    useEffect(() => {
        syncAutoLockedPosition();
    }, [syncAutoLockedPosition]);

    useEffect(() => {
        if (hasBeenManuallyMoved || !toolbarRef.current?.parentElement || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            syncAutoLockedPosition();
        });

        observer.observe(toolbarRef.current.parentElement);
        return () => observer.disconnect();
    }, [hasBeenManuallyMoved, syncAutoLockedPosition]);

    const onMouseDown = (e) => {
        // Don't start drag setup for mode-switch buttons or other interactive elements —
        // these are click targets, not drag handles, and setting pointer-events:none on
        // the flow area would block subsequent canvas clicks.
        if (e.target.closest('.mode-switch-button') || e.target.closest('.mode-switcher-holder')) {
            return;
        }

        isDragging.current = true;
        dragStartedRef.current = false;
        pointerDownStartRef.current = { x: e.clientX, y: e.clientY };
        const rect = toolbarRef.current.getBoundingClientRect();
        dragOffset.current = {
            x: e.clientX - (rect.left + rect.width / 2),
            y: e.clientY - (rect.top + rect.height / 2),
        };
        e.stopPropagation();
        e.preventDefault();

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        // set cursor to grabbing
        document.body.style.cursor = 'grabbing';
        toolbarRef.current.style.cursor = 'grabbing';

        // Prevent the flow area from capturing mouse events while dragging so it doesn't interfere
        const flowAreaElement = document.getElementById('flow-area-wrapper');
        if (flowAreaElement) {
            flowAreaElement.style.pointerEvents = 'none';
        }
    };

    const onMouseMove = (e) => {
        if (isDragging.current) {
            const deltaX = e.clientX - pointerDownStartRef.current.x;
            const deltaY = e.clientY - pointerDownStartRef.current.y;
            if (!dragStartedRef.current && Math.hypot(deltaX, deltaY) >= DRAG_START_THRESHOLD) {
                dragStartedRef.current = true;
                setHasBeenManuallyMoved(true);
            }

            const parentRect = toolbarRef.current.parentElement.getBoundingClientRect();
            const desiredX = e.clientX - parentRect.left - dragOffset.current.x;
            const desiredY = e.clientY - parentRect.top - dragOffset.current.y;
            const nextPosition = getConstrainedPosition(desiredX, desiredY);
            if (nextPosition) {
                setToolbarX(nextPosition.x);
                setToolbarY(nextPosition.y);
            }
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const onMouseUp = (e) => {
        // Always restore pointer events regardless of drag state — this is a safety
        // net in case onMouseDown set them to none but isDragging was lost (e.g. due
        // to a re-render producing a new function reference between the two events).
        const flowAreaElement = document.getElementById('flow-area-wrapper');
        if (flowAreaElement) {
            flowAreaElement.style.pointerEvents = '';
        }

        if (isDragging.current) {
            isDragging.current = false;
            dragStartedRef.current = false;
            e.stopPropagation();
            e.preventDefault();

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            // reset cursor
            document.body.style.cursor = 'default';
            if (toolbarRef.current) toolbarRef.current.style.cursor = 'grab';
        }
    }

    // console.log('Rendering FloatingToolbar at ', toolbarX, toolbarY, props);

    return (
        <div 
            className="timeline-editor-floating-toolbar"
            style={{
                left: toolbarX,
                top: toolbarY,
            }}
            ref={toolbarRef}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
        >
            {/* <img src={HandStop} className="icon" alt="Hand Stop" /> */}
            {/* <div className="icon-divider" />
            <img src={Select} className="icon" alt="Select" /> */}
            {/* <div className="icon-divider" /> */}
            <img src={Undo} className={`icon ${props.undoStackLength === 0 ? 'disabled' : ''}`} alt="Undo" onClick={(e) => {e.stopPropagation(); props.undoLastAction && props.undoLastAction();}} />
            <img src={Redo} className={`icon ${props.redoStackLength === 0 ? 'disabled' : ''}`} alt="Redo" onClick={(e) => {e.stopPropagation(); props.redoLastAction && props.redoLastAction();}} />
            <div className="icon-divider" />

            <div className="mode-switcher-holder">
                <div className={`mode-switch-button ${props.currentMode === MODES.FLOW_EDITOR ? 'active' : ''}`} onClick={(e) => {e.stopPropagation(); switchMode(MODES.FLOW_EDITOR)}}>Flow</div>
                <div className={`mode-switch-button ${props.currentMode === MODES.CANVAS_EDITOR ? 'active' : ''} ${(isCombinedMode || !canOpenCanvas) ? 'disabled' : ''}`} onClick={(e) => {
                    e.stopPropagation();
                    if (isCombinedMode || !canOpenCanvas) {
                        return;
                    }
                    switchMode(MODES.CANVAS_EDITOR);
                }}>Canvas</div>
                <div className={`mode-switch-button ${props.currentMode === MODES.EXPANDED_FLOW ? 'active' : ''} ${(isCombinedMode || !canOpenComponentFlow) ? 'disabled' : ''}`} onClick={handleComponentFlowClick}>Component Flow</div>
            </div>
        </div>
    );
};

export default FloatingToolbar;